import moment from 'moment'

import { i18nDateToString } from 'modules/core/lib/dates'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import type { View, UnassignedShift } from 'modules/shift_management/lib/types'
import type { DragItem } from 'modules/shift_management/hooks/useDragAndDrop'
import { NO_WORK_AREA } from 'modules/shift_management/store/internal/models/Shift'
import type {
  ClipboardSelection,
  GridDragAndDropAdapter,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type { ClipboardHandlers } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import type {
  ExtendedGridEventAppearance,
  GridClipboardPayload,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'

type GridEntity = { id: string; selectable?: boolean }

type ShiftManagementDragItemPayload = {
  clipboardPayload: GridClipboardPayload
  sourceEntityId: string
  sourceStart: Date
}

const isShiftManagementDragItemPayload = (
  payload: unknown
): payload is ShiftManagementDragItemPayload => {
  if (typeof payload !== 'object' || payload === null) return false

  const clipboardPayload = Reflect.get(payload, 'clipboardPayload')
  const sourceEntityId = Reflect.get(payload, 'sourceEntityId')
  const sourceStart = Reflect.get(payload, 'sourceStart')

  return (
    typeof sourceEntityId === 'string' &&
    sourceStart instanceof Date &&
    typeof clipboardPayload === 'object' &&
    clipboardPayload !== null
  )
}

const isLegacyUnassignedShiftDragItem = (item: unknown): item is DragItem => {
  if (typeof item !== 'object' || item === null) return false
  return 'unassignedShift' in item && Reflect.get(item, 'unassignedShift') != null
}

const createSingleCellSelection = (
  entity: GridEntity,
  slot: { start: Date; end: Date; index: number },
  events: ExtendedGridEventAppearance[]
): ClipboardSelection<GridEntity, ExtendedGridEventAppearance> => ({
  anchor: {
    entity,
    slot,
    events,
  },
  cells: [
    {
      context: {
        entity,
        slot,
        events,
      },
      rowOffset: 0,
      columnOffset: 0,
    },
  ],
})

interface OnDropUnassignedShiftArgs {
  unassignedShift: UnassignedShift
  targetEntityId: string
  targetDate: moment.Moment
}

interface UseGridDragAndDropAdapterArgs {
  clipboardHandlers?: ClipboardHandlers<GridEntity, ExtendedGridEventAppearance>
  view: View
  onDropUnassignedShift?: (args: OnDropUnassignedShiftArgs) => void | Promise<void>
}

const useGridDragAndDropAdapter = ({
  clipboardHandlers,
  view,
  onDropUnassignedShift,
}: UseGridDragAndDropAdapterArgs): GridDragAndDropAdapter<
  GridEntity,
  ExtendedGridEventAppearance
> =>
  useUnsafeMemo(
    () => ({
      acceptedTypes: ['shift_management:grid_event', 'unassignedShift'],
      getDragItem: (context) => {
        if (!context.event.clipboardPayload) return null

        return {
          type: 'shift_management:grid_event',
          payload: {
            clipboardPayload: context.event.clipboardPayload,
            sourceEntityId: context.entity.id,
            sourceStart: context.event.start,
          } satisfies ShiftManagementDragItemPayload,
        }
      },
      canDrag: (context) => context.event.selectable,
      canDrop: (item, target) => {
        // Legacy unassigned shift from CoverageSection
        if (isLegacyUnassignedShiftDragItem(item)) {
          if (target.entity.selectable === false) return false
          const targetDate = i18nDateToString(moment(target.slot.start), 'backend')
          return item.date === targetDate
        }

        if (!isShiftManagementDragItemPayload(item.payload)) return false
        const payload = item.payload
        if (target.entity.selectable === false) return false

        const isSameEntity = payload.sourceEntityId === target.entity.id
        const sourceDate = i18nDateToString(moment(payload.sourceStart), 'backend')
        const targetDate = i18nDateToString(moment(target.slot.start), 'backend')

        if (!view.isDaily) return !(isSameEntity && sourceDate === targetDate)

        const sourceTime = i18nDateToString(moment(payload.sourceStart), 'ui_time')
        const targetTime = i18nDateToString(moment(target.slot.start), 'ui_time')
        return !(isSameEntity && sourceDate === targetDate && sourceTime === targetTime)
      },
      onDrop: async (item, target) => {
        // Legacy unassigned shift from CoverageSection
        if (isLegacyUnassignedShiftDragItem(item)) {
          if (!onDropUnassignedShift || !item.unassignedShift) return
          await onDropUnassignedShift({
            unassignedShift: item.unassignedShift,
            targetEntityId: target.entity.id,
            targetDate: moment(target.slot.start),
          })
          return
        }

        if (!isShiftManagementDragItemPayload(item.payload)) return
        const payload = item.payload
        if (!clipboardHandlers) return

        let dropOverridesByOffset:
          | Record<
              string,
              {
                startTime?: string | null
                locationId?: number | null
                workAreaId?: number | null
              }
            >
          | undefined

        const offsetKey = `${target.entity.id}:${target.slot.index}`
        const startTime = view.isDaily
          ? i18nDateToString(moment(target.slot.start), 'ui_time')
          : undefined

        let locationId: number | null | undefined
        let workAreaId: number | null | undefined

        if (view.isByAreas) {
          const [locationKey, workAreaKey] = target.entity.id.split('-')
          locationId = locationKey ? Number(locationKey) : null
          workAreaId = workAreaKey && workAreaKey !== NO_WORK_AREA ? Number(workAreaKey) : null
        }

        if (startTime !== undefined || view.isByAreas) {
          dropOverridesByOffset = {
            [offsetKey]: {
              startTime,
              locationId: locationId ?? null,
              workAreaId: workAreaId ?? null,
            },
          }
        }

        const sourceSelection = createSingleCellSelection(
          { id: payload.sourceEntityId },
          {
            start: payload.sourceStart,
            end: payload.sourceStart,
            index: 0,
          },
          []
        )
        const targetSelection = createSingleCellSelection(target.entity, target.slot, target.events)

        await clipboardHandlers.onPaste({
          source: sourceSelection,
          target: targetSelection,
          payload: [{ rowOffset: 0, columnOffset: 0, payload: [payload.clipboardPayload] }],
          mode: 'move',
          replaceTargetContent: false,
          dropOverridesByOffset,
        })
      },
    }),
    [clipboardHandlers, view, onDropUnassignedShift]
  )

export { useGridDragAndDropAdapter }
